import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";

export interface BillingProject {
  id: string;
  name: string;
  clientName?: string | null;
  ownerUserId: string;
  isActive: boolean;
  createdAt: string;
}

export interface BillingProjectTask {
  id: string;
  projectId: string;
  name: string;
  defaultRate?: number | null;
  isActive: boolean;
  sortOrder: number;
  createdAt: string;
}

export function useBillingProjects() {
  return useQuery({
    queryKey: ["billing-projects"],
    queryFn: async () => {
      const r = await api().get<BillingProject[]>("/api/billing-projects");
      return r.data;
    },
  });
}

export function useProjectTasks(projectId: string | null) {
  return useQuery({
    queryKey: ["billing-projects", projectId, "tasks"],
    queryFn: async () => {
      const r = await api().get<BillingProjectTask[]>(`/api/billing-projects/${projectId}/tasks`);
      return r.data;
    },
    enabled: !!projectId,
  });
}

export function useCreateBillingProject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: { name: string; clientName?: string }) => {
      const r = await api().post<BillingProject>("/api/billing-projects", data);
      return r.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["billing-projects"] });
    },
  });
}

export function useCreateProjectTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: { projectId: string; name: string; defaultRate?: number }) => {
      const r = await api().post<BillingProjectTask>(`/api/billing-projects/${data.projectId}/tasks`, {
        name: data.name,
        defaultRate: data.defaultRate,
      });
      return r.data;
    },
    onSuccess: (_, variables) => {
      qc.invalidateQueries({ queryKey: ["billing-projects", variables.projectId, "tasks"] });
    },
  });
}
